import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "./ui/dialog";
import {
  Users, Compass, Building2, FileText, Search, UserPlus, Check, X,
  Plus, Loader2, Trash2, BadgeCheck, Ship, ChevronDown,
} from "lucide-react";
import { useToast } from "../hooks/use-toast";

const Avatar = ({ name, color = "#1e293b" }) => (
  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-black text-white" style={{ background: color }}>
    {(name || "?").charAt(0).toUpperCase()}
  </span>
);

const Card = ({ icon: Icon, title, count, action, children, testid }) => (
  <div className="rounded-2xl border border-gray-200 bg-white p-5" data-testid={testid}>
    <div className="flex items-center justify-between gap-2">
      <div className="flex items-center gap-2 font-bold text-gray-900">
        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100"><Icon className="h-5 w-5 text-slate-600" /></span>
        <div>
          <div className="text-sm font-extrabold">{title}</div>
          {count != null && <div className="text-xs font-normal text-gray-500">{count}</div>}
        </div>
      </div>
      {action}
    </div>
    <div className="mt-3">{children}</div>
  </div>
);

const ScrollList = ({ children }) => (
  <div className="max-h-64 space-y-2 overflow-y-auto pr-1">{children}</div>
);

// Instant filter box styled like the friends search
const ListSearch = ({ value, onChange, placeholder, testid }) => (
  <div className="mb-3 flex h-10 items-center gap-2 rounded-md border border-input bg-white px-3 focus-within:ring-2 focus-within:ring-slate-300">
    <Search className="h-4 w-4 shrink-0 text-gray-400" />
    <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid}
      className="min-w-0 flex-1 bg-transparent text-sm text-gray-800 placeholder:text-muted-foreground focus:outline-none" />
    {value && (
      <button type="button" onClick={() => onChange("")} aria-label="Clear search" data-testid={`${testid}-clear`} className="text-gray-400 hover:text-gray-600">
        <X className="h-3.5 w-3.5" />
      </button>
    )}
  </div>
);


const RightSidebar = ({ refreshPostsSignal }) => {
  const { toast } = useToast();
  const navigate = useNavigate();

  // friends
  const [q, setQ] = useState("");
  const [commQ, setCommQ] = useState("");
  const [compQ, setCompQ] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [friends, setFriends] = useState([]);
  const [requests, setRequests] = useState([]);

  // communities & companies & posts
  const [communities, setCommunities] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [myPosts, setMyPosts] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [newComm, setNewComm] = useState({ name: "", description: "" });
  const [savingComm, setSavingComm] = useState(false);
  const [nameCheck, setNameCheck] = useState(null); // null | {checking} | {available, reason}
  const [open, setOpen] = useState(null);

  useEffect(() => {
    const name = newComm.name.trim();
    if (!createOpen || name.length < 2) { setNameCheck(null); return; }
    setNameCheck({ checking: true });
    const t = setTimeout(async () => {
      try {
        const res = (await api.get(`/communities/check-name?name=${encodeURIComponent(name)}`)).data;
        setNameCheck(res);
      } catch { setNameCheck(null); }
    }, 400);
    return () => clearTimeout(t);
  }, [newComm.name, createOpen]);

  const loadFriends = useCallback(async () => {
    try {
      const [f, r] = await Promise.all([api.get("/friends"), api.get("/friends/requests")]);
      setFriends(f.data.friends); setRequests(r.data.requests);
    } catch { /* ignore */ }
  }, []);
  const loadCommunities = useCallback(async () => {
    try { setCommunities((await api.get("/communities")).data.communities); } catch { /* ignore */ }
  }, []);
  const loadCompanies = useCallback(async () => {
    try { setCompanies((await api.get("/companies")).data.companies); } catch { /* ignore */ }
  }, []);
  const loadPosts = useCallback(async () => {
    try { setMyPosts((await api.get("/posts/mine")).data.posts); } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadFriends(); loadCommunities(); loadCompanies(); loadPosts(); }, [loadFriends, loadCommunities, loadCompanies, loadPosts]);
  useEffect(() => { loadPosts(); }, [refreshPostsSignal, loadPosts]);

  const doSearch = async () => {
    if (q.trim().length < 3) { toast({ title: "Type at least 3 characters (email or phone)" }); return; }
    setSearching(true);
    try {
      setSearchResults((await api.get(`/friends/search?q=${encodeURIComponent(q)}`)).data.results);
    } catch (e) {
      toast({ title: "Search failed", description: e?.response?.data?.detail, variant: "destructive" });
    } finally { setSearching(false); }
  };

  const sendRequest = async (person) => {
    try {
      await api.post("/friends/request", { to_user_id: person.user_id });
      setSearchResults((r) => r.map((x) => (x.user_id === person.user_id ? { ...x, status: "requested" } : x)));
      loadFriends();
      toast({ title: "Friend request sent" });
    } catch (e) {
      toast({ title: "Could not send request", description: e?.response?.data?.detail, variant: "destructive" });
    }
  };

  const respond = async (req, accept) => {
    try {
      await api.post("/friends/respond", { request_id: req.request_id, accept });
      toast({ title: accept ? "Friend added" : "Request declined" });
      loadFriends();
    } catch { toast({ title: "Action failed", variant: "destructive" }); }
  };

  const unfriend = async (f) => {
    try {
      await api.delete(`/friends/${f.request_id}`);
      setFriends((l) => l.filter((x) => x.user_id !== f.user_id));
    } catch { toast({ title: "Action failed", variant: "destructive" }); }
  };

  const toggleCommunity = async (c) => {
    const join = !c.joined;
    try {
      await api.post(`/communities/${c.id}/${join ? "join" : "leave"}`);
      loadCommunities();
    } catch { toast({ title: "Action failed", variant: "destructive" }); }
  };

  const createCommunity = async () => {
    if (newComm.name.trim().length < 2) { toast({ title: "Name is too short" }); return; }
    setSavingComm(true);
    try {
      await api.post("/communities", { name: newComm.name.trim(), description: newComm.description });
      setCreateOpen(false); setNewComm({ name: "", description: "" });
      loadCommunities();
      toast({ title: "Community created" });
    } catch (e) {
      toast({ title: "Could not create", description: e?.response?.data?.detail, variant: "destructive" });
    } finally { setSavingComm(false); }
  };

  const toggleCompany = async (c) => {
    const follow = !c.following;
    try {
      await api.post(`/companies/${c.id}/${follow ? "follow" : "unfollow"}`);
      loadCompanies();
    } catch { toast({ title: "Action failed", variant: "destructive" }); }
  };

  const deletePost = async (p) => {
    try {
      await api.delete(`/posts/${p.id}`);
      setMyPosts((l) => l.filter((x) => x.id !== p.id));
      toast({ title: "Post deleted" });
    } catch { toast({ title: "Delete failed", variant: "destructive" }); }
  };

  const sections = [
    { id: "friends", icon: Users, title: "My Friends", count: `${friends.length} connected` },
    { id: "communities", icon: Compass, title: "My Communities", count: `${communities.filter((c) => c.joined).length} joined`,
      action: <Button onClick={() => setCreateOpen(true)} data-testid="community-create-button" className="h-8 rounded-full bg-[#FF4500] px-3 text-xs font-bold hover:bg-[#e03d00]"><Plus className="mr-1 h-3.5 w-3.5" /> Create</Button> },
    { id: "companies", icon: Building2, title: "My Companies", count: `${companies.filter((c) => c.following).length} followed` },
    { id: "posts", icon: FileText, title: "My Posts", count: `${myPosts.length} posted` },
  ];
  const current = sections.find((s) => s.id === open);
  const matches = (text, term) => (text || "").toLowerCase().includes(term.trim().toLowerCase());
  const shownCommunities = communities.filter((c) => matches(c.name, commQ));
  const shownCompanies = companies.filter((c) => matches(c.name, compQ) || matches(c.organization, compQ));

  const bodies = {
    friends: (
      <>
        <div className="flex gap-2" data-testid="rs-friends-search">
          <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && doSearch()}
            placeholder="Search by email or phone" className="h-10" data-testid="friend-search-input" />
          <Button onClick={doSearch} disabled={searching} data-testid="friend-search-button"
            className="h-10 shrink-0 rounded-full border border-gray-200 bg-white px-3 text-gray-700 hover:bg-slate-50">
            {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
          </Button>
        </div>

        {searchResults && (
          <div className="mt-3 space-y-2">
            {searchResults.length === 0 && <p className="text-xs text-gray-400" data-testid="friend-search-empty">No one found for "{q}".</p>}
            {searchResults.map((p) => (
              <div key={p.user_id} className="flex items-center gap-2" data-testid={`friend-result-${p.user_id}`}>
                <Avatar name={p.name} />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-bold text-gray-800">{p.name}</div>
                  <div className="truncate text-xs text-gray-400 capitalize">{p.rank || p.role}</div>
                </div>
                {p.status === "friends" ? (
                  <span className="text-xs font-bold text-[#002855]">Friends</span>
                ) : p.status === "requested" ? (
                  <span className="text-xs font-semibold text-gray-400">Requested</span>
                ) : (
                  <Button onClick={() => sendRequest(p)} data-testid={`friend-add-${p.user_id}`}
                    className="h-8 rounded-full bg-[#FF4500] px-3 text-xs font-bold hover:bg-[#e03d00]">
                    <UserPlus className="mr-1 h-3.5 w-3.5" /> Add
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}

        {requests.length > 0 && (
          <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 p-3" data-testid="rs-friend-requests">
            <div className="text-xs font-extrabold uppercase tracking-wide text-slate-500">Requests ({requests.length})</div>
            <div className="mt-2 space-y-2">
              {requests.map((r) => (
                <div key={r.request_id} className="flex items-center gap-2" data-testid={`friend-request-${r.user_id}`}>
                  <Avatar name={r.name} />
                  <div className="min-w-0 flex-1 truncate text-sm font-bold text-gray-800">{r.name}</div>
                  <Button onClick={() => respond(r, true)} data-testid={`friend-accept-${r.user_id}`} className="h-8 w-8 rounded-full border border-emerald-200 bg-emerald-50 p-0 text-emerald-700 hover:bg-emerald-100"><Check className="h-4 w-4" /></Button>
                  <Button onClick={() => respond(r, false)} variant="outline" data-testid={`friend-decline-${r.user_id}`} className="h-8 w-8 rounded-full p-0"><X className="h-4 w-4" /></Button>
                </div>
              ))}
            </div>
          </div>
        )}

        <ScrollList>
          {friends.length === 0 && <p className="pt-2 text-xs text-gray-400">No friends yet — search above to connect.</p>}
          {friends.map((f) => (
            <div key={f.user_id} className="group flex items-center gap-2" data-testid={`friend-${f.user_id}`}>
              <Avatar name={f.name} />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1 truncate text-sm font-bold text-gray-800">{f.name} <BadgeCheck className="h-3 w-3 text-[#002855]" /></div>
                <div className="truncate text-xs text-gray-400 capitalize">{f.rank || f.role}</div>
              </div>
              <button onClick={() => unfriend(f)} data-testid={`friend-remove-${f.user_id}`} className="rounded-full p-1.5 text-gray-300 opacity-0 transition-opacity hover:text-red-500 group-hover:opacity-100"><X className="h-4 w-4" /></button>
            </div>
          ))}
        </ScrollList>
      </>
    ),
    communities: (
      <>
        <ListSearch value={commQ} onChange={setCommQ} placeholder="Search communities" testid="community-search-input" />
        <ScrollList>
          {shownCommunities.length === 0 && <p className="text-xs text-gray-400" data-testid="community-search-empty">{commQ ? `No communities match "${commQ}".` : "No communities yet."}</p>}
          {shownCommunities.map((c) => (
            <div key={c.id} className="flex items-center gap-2" data-testid={`community-${c.id}`}>
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-slate-100 text-xs font-black text-slate-700">{c.name.charAt(0).toUpperCase()}</span>
              <button onClick={() => navigate(`/c/${encodeURIComponent(c.name)}`)} data-testid={`community-open-${c.id}`} className="min-w-0 flex-1 text-left">
                <div className="truncate text-sm font-bold text-gray-800 hover:underline">{c.name} {c.is_owner && <span className="text-[10px] font-bold text-[#FF4500]">· owner</span>}</div>
                <div className="truncate text-xs text-gray-400">{c.member_count.toLocaleString()} members</div>
              </button>
              <Button onClick={() => toggleCommunity(c)} data-testid={`community-toggle-${c.id}`} variant={c.joined ? "outline" : "default"}
                className={`h-8 rounded-full px-3 text-xs font-bold ${c.joined ? "border-slate-200 bg-slate-50 text-slate-600" : "border border-slate-200 bg-white text-slate-700 hover:bg-slate-50"}`}>
                {c.joined ? "Joined" : "Follow"}
              </Button>
            </div>
          ))}
        </ScrollList>
      </>
    ),
    companies: (
      <>
        <ListSearch value={compQ} onChange={setCompQ} placeholder="Search companies" testid="company-search-input" />
        <ScrollList>
          {companies.length === 0 && <p className="text-xs text-gray-400" data-testid="companies-empty">No shipping companies have joined yet.</p>}
          {companies.length > 0 && shownCompanies.length === 0 && <p className="text-xs text-gray-400" data-testid="company-search-empty">No companies match "{compQ}".</p>}
          {shownCompanies.map((c) => (
            <div key={c.id} className="flex items-center gap-2" data-testid={`company-${c.id}`}>
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-600"><Ship className="h-4 w-4" /></span>
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-bold text-gray-800">{c.name}</div>
                <div className="truncate text-xs text-gray-400">{c.fleet}</div>
              </div>
              <Button onClick={() => toggleCompany(c)} data-testid={`company-toggle-${c.id}`} variant={c.following ? "outline" : "default"}
                className={`h-8 rounded-full px-3 text-xs font-bold ${c.following ? "border-slate-200 bg-slate-50 text-slate-600" : "border border-[#FF4500]/30 bg-white text-[#FF4500] hover:bg-orange-50"}`}>
                {c.following ? "Following" : "Follow"}
              </Button>
            </div>
          ))}
        </ScrollList>
      </>
    ),
    posts: (
        <ScrollList>
          {myPosts.length === 0 && <p className="text-xs text-gray-400" data-testid="posts-empty">You haven't posted yet. Use the box in the feed to share something.</p>}
          {myPosts.map((p) => (
            <div key={p.id} className="group rounded-xl border border-gray-100 bg-gray-50/60 p-3" data-testid={`my-post-${p.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="truncate text-sm font-bold text-gray-800">{p.title}</div>
                  <div className="text-xs text-gray-400">{p.community} · {p.upvotes ?? 1} upvotes</div>
                </div>
                <button onClick={() => deletePost(p)} data-testid={`my-post-delete-${p.id}`} className="rounded-full p-1.5 text-gray-300 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
        </ScrollList>
    ),
  };

  return (
    <section className="mt-4" data-testid="right-sidebar">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4" role="tablist" aria-label="Your network">
        {sections.map((s) => {
          const on = open === s.id;
          const Icon = s.icon;
          return (
            <button key={s.id} type="button" role="tab" aria-selected={on} onClick={() => setOpen(on ? null : s.id)}
              data-testid={`rs-tab-${s.id}`}
              className={`flex items-center gap-2.5 rounded-2xl border px-3 py-2.5 text-left transition-colors duration-200 ${on ? "border-[#FF4500]/40 bg-orange-50/60 text-gray-900" : "border-gray-200 bg-white text-gray-900 hover:border-slate-300 hover:bg-slate-50"}`}>
              <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${on ? "bg-white" : "bg-slate-100"}`}>
                <Icon className={`h-4 w-4 ${on ? "text-[#FF4500]" : "text-slate-600"}`} />
              </span>
              <span className="min-w-0">
                <span className="block truncate text-sm font-extrabold">{s.id === "posts" ? s.title : s.title.replace("My ", "")}</span>
                <span className={`block truncate text-xs ${"text-gray-500"}`}>{s.count}</span>
              </span>
              <ChevronDown className={`ml-auto h-4 w-4 shrink-0 transition-transform duration-200 ${on ? "rotate-180 text-[#FF4500]" : "text-gray-400"}`} />
            </button>
          );
        })}
      </div>

      {current && (
        <div className="mt-2">
          <Card icon={current.icon} title={current.title} count={current.count} action={current.action} testid={`rs-${current.id}`}>
            {bodies[current.id]}
          </Card>
        </div>
      )}

      {/* Create community dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-md" aria-describedby={undefined} data-testid="create-community-dialog">
          <DialogHeader>
            <DialogTitle>Create a community</DialogTitle>
            <DialogDescription>Start a space for your rank, vessel type or topic.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-sm font-semibold text-gray-700">Community name</label>
              <Input value={newComm.name} onChange={(e) => setNewComm({ ...newComm, name: e.target.value })} placeholder="e.g. LNGMasters" data-testid="community-name-input" />
              {nameCheck?.checking && <p className="text-xs text-gray-400">Checking availability…</p>}
              {nameCheck && !nameCheck.checking && nameCheck.available && (
                <p className="text-xs font-bold text-green-600" data-testid="community-name-available">Name available</p>
              )}
              {nameCheck && !nameCheck.checking && !nameCheck.available && (
                <p className="text-xs font-bold text-red-500" data-testid="community-name-taken">
                  {nameCheck.reason === "too_short" ? "Name is too short" : "Name already taken — try another"}
                </p>
              )}
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-semibold text-gray-700">Description</label>
              <Input value={newComm.description} onChange={(e) => setNewComm({ ...newComm, description: e.target.value })} placeholder="What's this community about?" data-testid="community-desc-input" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={createCommunity} disabled={savingComm || !nameCheck?.available} data-testid="community-save-button" className="rounded-full bg-[#FF4500] font-bold hover:bg-[#e03d00]">
              {savingComm ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create community"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default RightSidebar;
